// ABOUTME: Provides shared formatting, comparison, reporting, and browser utility helpers.
// ABOUTME: Centralizes reusable transformations used across admin application features.
import { saveAs } from "file-saver";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import { DateRange } from "rsuite/esm/DateRangePicker";
import { Renderer } from "xlsx-renderer";
import { getApiBaseUrl } from "../config/env";
import { PaymentStatusEnum } from "../pages/common/types/payment-status";
import { SalesVoucherStatusEnum } from "../pages/common/types/salesvourcher-status-enum";

const startOfDay = (date: Date): Date => {
  if (!date) date = new Date();
  return new Date(date.setHours(0, 0, 0, 0));
};

const endOfDay = (date: Date): Date => {
  if (!date) date = new Date();
  return new Date(date.setHours(23, 59, 59, 999));
};

const addPrefixedValues = (result: any, prefix: string, values: any) => {
  for (const key in values) {
    result[`${prefix}.${key}`] = values[key];
  }
};

const addFlattenedArrayValues = (
  result: any,
  key: string,
  values: any[]
) => {
  for (let index = 0; index < values.length; index++) {
    addPrefixedValues(result, `${key}[${index}]`, flattenObj(values[index]));
  }
};

const flattenObj = (ob: any, flattenArray = false): any => {
  const result: any = {};
  for (const i in ob) {
    if (typeof ob[i] === "object" && !Array.isArray(ob[i])) {
      addPrefixedValues(result, i, flattenObj(ob[i]));
    } else if (Array.isArray(ob[i]) && flattenArray) {
      addFlattenedArrayValues(result, i, ob[i]);
    } else {
      result[i] = ob[i];
    }
  }
  return result;
};

const PDF_LOGO = `${getApiBaseUrl().origin}/assets/images/hmaterials.png`;

const getPdfPrintParams = () => {
  return {
    PDF_PAGE_ORITENTATION: "landscape",
    PDF_WITH_HEADER_IMAGE: true,
    PDF_WITH_FOOTER_PAGE_COUNT: true,
    PDF_HEADER_HEIGHT: 25,
    PDF_ROW_HEIGHT: 15,
    PDF_ODD_BKG_COLOR: "#fcfcfc",
    PDF_EVEN_BKG_COLOR: "#fff",
    PDF_WITH_CELL_FORMATTING: true,
    PDF_WITH_COLUMNS_AS_LINKS: true,
    PDF_SELECTED_ROWS_ONLY: true,
    PDF_HEADER_COLOR: "#f8f8f8",
    PDF_INNER_BORDER_COLOR: "#dde2eb",
    PDF_OUTER_BORDER_COLOR: "#babfc7",
    PDF_LOGO,
  };
};

async function exportExcel(excelTemplate, viewModel, exportName) {
  let rendererBuffer = await new Renderer().renderFromArrayBuffer(
    excelTemplate,
    viewModel
  );
  let buffer = await rendererBuffer.xlsx.writeBuffer();

  saveAs(new Blob([buffer]), exportName);
}

const toTitleCase = (text: string) => {
  let result = text.replace(/([A-Z][a-z])/g, " $1");
  let ignoreTexts = ["of"];
  for (const text of ignoreTexts) {
    const regex = new RegExp(text, "gi");
    result = result.replace(regex, text);
  }
  const finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
};

const checkIsDeliveryInclusive = (val: boolean, t: (string) => string) =>
  val ? t("labels.delivered") : t("labels.ex-works");

function dateComparator(date1, date2) {
  const date1Number = _monthToNum(date1);
  const date2Number = _monthToNum(date2);

  if (date1Number === null && date2Number === null) {
    return 0;
  }
  if (date1Number === null) {
    return -1;
  }
  if (date2Number === null) {
    return 1;
  }

  return date1Number - date2Number;
}

function arraysContainSameStrings(arr1, arr2) {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const sorted1 = arr1.slice().sort();
  const sorted2 = arr2.slice().sort();

  for (let i = 0; i < sorted1.length; i++) {
    if (sorted1[i] !== sorted2[i]) {
      return false;
    }
  }

  return true;
}

function formatNumber(number) {
  const formattedNumber = Number(number).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: true,
  });

  return formattedNumber
    .replace(".", ",")
    .replaceAll(",", " ")
    .replace("-", "- ");
}

const timeFilterComparator = {
  FilterButtonType: ["apply", "clear", "reset", "cancel"],
  debounceMs: 500,
  suppressAndOrCondition: true,
  inRangeInclusive: true,
  inRangeFloatingFilterDateFormat: "HH:mm",
  textMatcher: (evt) => {
    const filterTextLowerCase = evt.filterText.toLowerCase();
    const valueLowerCase = moment(evt.value.toString())
      .format("HH:mm")
      .toLowerCase();
    switch (evt.filterOption) {
      case "contains":
        return valueLowerCase.includes(filterTextLowerCase);
      case "notContains":
        return !valueLowerCase.includes(filterTextLowerCase);
      case "equals":
        return valueLowerCase === filterTextLowerCase;
      case "notEqual":
        return valueLowerCase !== filterTextLowerCase;
      case "startsWith":
        return valueLowerCase.startsWith(filterTextLowerCase);
      case "endsWith":
        return valueLowerCase.endsWith(filterTextLowerCase);
      default:
        console.warn("invalid filter type " + evt.filter);
        return false;
    }
  },
};

const formattedPTTime = (
  inputString: string,
  returns: "momentObject" | "string" = "string"
) => {
  const duration = moment.duration(inputString);
  const hours = Math.floor(duration.asHours());
  const minutes = Math.floor(duration.asMinutes()) - hours * 60;
  const seconds = duration.seconds();
  const formattedTime = moment().hours(hours).minutes(minutes).seconds(seconds);

  return returns === "string"
    ? formattedTime.format("hh:mm:ss A")
    : formattedTime;
};

const filterComparator = {
  FilterButtonType: ["apply", "clear", "reset", "cancel"],
  debounceMs: 500,
  suppressAndOrCondition: true,
  inRangeInclusive: true,
  inRangeFloatingFilterDateFormat: "DD/MM/YYYY",
  comparator: function (filterLocalDateAtMidnight, cellValue) {
    cellValue = moment(cellValue).format("DD/MM/YYYY");
    if (cellValue === null) {
      return 0;
    }
    const dateParts = cellValue.split("/");
    const year = Number(dateParts[2]);
    const month = Number(dateParts[1]) - 1;
    const day = Number(dateParts[0]);
    const cellDate = new Date(year, month, day);

    if (cellDate < filterLocalDateAtMidnight) {
      return -1;
    } else if (cellDate > filterLocalDateAtMidnight) {
      return 1;
    } else {
      return 0;
    }
  },
};

function _monthToNum(date) {
  if (date?.length !== 10) {
    return null;
  }

  const yearNumber = date.substring(6, 10);
  const monthNumber = date.substring(3, 5);
  const dayNumber = date.substring(0, 2);

  const result = yearNumber * 10000 + monthNumber * 100 + dayNumber;
  return result;
}

function dateFormatter(params) {
  const dateAsString = params.data.date;
  const dateParts = dateAsString.split("/");
  return `${dateParts[0]} - ${dateParts[1]} - ${dateParts[2]}`;
}

const initializePaddedValue = (value: any, image: any) => {
  if (value) {
    return value;
  }
  if (typeof image !== "object") {
    return "";
  }
  return image?.forEach ? [] : {};
};

function paddObject(obj: any, image: any) {
  obj = cloneDeep(obj);
  for (const key in image) {
    const imageValue = image[key];
    if (typeof imageValue !== "object") {
      if (obj && !obj[key]) {
        obj[key] = "";
      }
      continue;
    }
    if (imageValue?.forEach && imageValue.length > 0) {
      if (!obj[key]) obj[key] = [];

      const arrayImage = imageValue[0];
      obj[key].forEach((item: any, index: number) => {
        obj[key][index] = initializePaddedValue(item, arrayImage);
        paddObject(obj[key][index], arrayImage);
      });
      continue;
    }
    if (!imageValue?.forEach) {
      obj[key] = initializePaddedValue(obj[key], imageValue);
      paddObject(obj[key], imageValue);
    }
  }
  return obj;
}

function wait(timeout: number) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve("");
    }, timeout);
  });
}

const attachGridScrollEvent = (
  data: any[],
  loading: boolean,
  totalCount: number,
  loadMore: () => void,
  type: "grid" | "table" = "table",
  elementClass: string | undefined = undefined
) => {
  let scroller = document.querySelector<HTMLElement>(
    elementClass ??
      (type === "table"
        ? ".MuiTableContainer-root"
        : ".MuiDataGrid-virtualScroller")
  );
  if (scroller) {
    scroller.onscroll = (event) => {
      if (loading) return;
      let totalUserCount = totalCount ?? 0;
      let scrollHeight = (event.target as HTMLElement)?.scrollHeight;
      let clientHeight = (event.target as HTMLElement)?.clientHeight;
      let scrollTop = (event.target as HTMLElement)?.scrollTop;
      if (
        clientHeight + scrollTop + 20 >= scrollHeight &&
        Number(data?.length ?? 0) < totalUserCount
      ) {
        loadMore();
      }
    };
  }
};
const persistLastPOLocation = (pathName: string) => {
  localStorage.setItem("lastPillNavLocation", pathName);
};

const getLastPOLocation = () => {
  return localStorage.getItem("lastPillNavLocation");
};

const compareObjects = (obj1: object, obj2: object) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

const convertToISO8601 = (date: Date = new Date()) => {
  return moment
    .duration({ hours: date?.getHours(), minutes: date?.getMinutes() })
    .toISOString();
};

const convertfromISO8601 = (date: string): Date => {
  return moment("2023-04-11T00:00:00Z").add(moment.duration(date)).toDate();
};

const objectDateComparator = (dateProperty: string) => {
  return (a, b) =>
    new Date(a?.[dateProperty] ?? "").getTime() -
    new Date(b?.[dateProperty] ?? "").getTime();
};

const convertFromPascal = (text: string) => {
  if (!text) return text;

  let temp = text
    .replace(/(?:^|\.?)([A-Z])/g, function (x, y) {
      return " " + y;
    })
    .replace(/^_/, "")
    .trim();

  if (temp.toLowerCase() === "local administrator") {
    temp = "Administrator";
  }

  return temp;
};

function calculateTimeDifferenceFormatted(
  startDate: string,
  endDate: string
): string {
  const start = moment(startDate);
  const end = moment(endDate);

  const duration = moment.duration(end.diff(start));
  const hours = duration.hours();
  const minutes = duration.minutes();

  return `${hours}h ${minutes}m`;
}

function calculateTimeDifferenceInMinutes(
  datetime1: string,
  datetime2: string
): number {
  const date1 = moment(datetime1);
  const date2 = moment(datetime2);

  const diffMinutes = date2.diff(date1, "minutes");

  return diffMinutes;
}

const calculateTimeDifference = (start: string, end: string): string => {
  const format = "HH:mm:ss";
  const startTime = moment(start, format);
  const endTime = moment(end, format);
  const duration = moment.duration(endTime.diff(startTime));
  const hours = Math.abs(Math.floor(duration.asHours()))
    .toString()
    .padStart(2, "0");
  const minutes = Math.abs(duration.minutes()).toString().padStart(2, "0");
  const seconds = Math.abs(duration.seconds()).toString().padStart(2, "0");
  if (
    Number.isNaN(+hours) ||
    Number.isNaN(+minutes) ||
    Number.isNaN(+seconds)
  ) {
    return "N/a";
  } else {
    return `${hours}:${minutes}:${seconds}`;
  }
};

const ellipsiceText = (data: string, capLength?: number) => {
  capLength = capLength ?? data.length;
  let lengthDifference = capLength - data.length;
  return (
    data.substring(0, capLength) +
    new Array(Math.max(0, lengthDifference)).fill(" ").join("") +
    (data[capLength] ? "..." : "   ")
  );
};

const addDateToWeek = (
  weeks: { [key: string]: string[] },
  date: Date,
  startDay: number,
  expectedWeekYear: number
) => {
  if (Number.isNaN(date.getDay())) {
    return;
  }

  const week = getWeekNumber(date, startDay);
  if (week.year !== expectedWeekYear) {
    return;
  }

  weeks[week.week] ??= [];
  const dateString = date.toDateString();
  if (!weeks[week.week].includes(dateString)) {
    weeks[week.week].push(dateString);
  }
};

function getWeeksOfYear(yearString, startDay): { [key: string]: string[] } {
  const year = Number.parseInt(yearString, 10);

  if (Number.isNaN(year)) {
    throw new TypeError("Invalid year input");
  }

  if (startDay < 0 || startDay > 6) {
    throw new Error(
      "Invalid start day; it should be between 0 (Sunday) and 6 (Saturday)"
    );
  }

  const weeks: { [key: string]: string[] } = {};

  for (let month = 0; month < 12; month++) {
    for (let day = 1; day <= 31; day++) {
      const date = new Date(year, month, day);
      if (date.getFullYear() === year) {
        addDateToWeek(weeks, date, startDay, year);
      }
    }
  }

  for (let day = 1; day <= 7; day++) {
    const date = new Date(year + 1, 0, day);
    addDateToWeek(weeks, date, startDay, year);
  }

  return weeks;
}

function shiftDayByOne(day: number) {
  return day === 6 ? 0 : day + 1;
}

function getFirstDayOfWeekOfYear(year, dayOfWeek) {
  const january1st = new Date(year, 0, 1);
  const startDayOfWeek =
    (shiftDayByOne(dayOfWeek) - january1st.getDay() + 7) % 7;

  return new Date(year, 0, 1 + startDayOfWeek);
}

function getWeekNumber(date, startDay): { year: number; week: number } {
  const january1st = getFirstDayOfWeekOfYear(date.getFullYear(), startDay);
  const january1stDay = (january1st.getDay() - startDay + 7) % 7;

  const days = Math.floor((Number(date) - Number(january1st)) / 86400000);
  const weekNumber = Math.ceil((days + january1stDay) / 7);
  if (weekNumber == 0) {
    const prevYear = date.getFullYear() - 1;
    return {
      year: prevYear,
      week: getWeekNumber(new Date(prevYear, 11, 31), startDay).week,
    };
  } else {
    return { year: date.getFullYear(), week: weekNumber };
  }
}

function contructDaysArrayWithStartDay(startDay: number) {
  const days: number[] = [];
  for (let i = startDay; i < startDay + 7; i++) {
    if (i > 6) {
      days.push(i % 7);
    } else {
      days.push(i);
    }
  }
  return days;
}

function getDateFromWeek(
  weekNumber: number,
  year: number,
  day: number,
  startDay
) {
  const weeksOfYear = getWeeksOfYear(year, startDay);

  const dateContruct = contructDaysArrayWithStartDay(startDay);

  const dayIndex = dateContruct.findIndex((i) => i == day);

  const dateString = weeksOfYear[weekNumber][dayIndex];

  return new Date(dateString);
}

const delayedReset = (action, time?: number) => {
  action(true);
  setTimeout(() => {
    action(false);
  }, time ?? 100);
};

const getDay = (date: Date) => {
  let day = date.getDay();
  if (day === 0) {
    day = 6;
  } else {
    day -= 1;
  }
  return day;
};

function getDateFromWeekxx(weekNumber, year, day) {
  const januaryFirst = new Date(year, 0, 1);

  const daysToTargetWeek = (weekNumber - 1) * 7 + day;

  const targetDate = new Date(
    januaryFirst.getTime() + daysToTargetWeek * 24 * 60 * 60 * 1000
  );

  const januaryFirstDay = januaryFirst.getDay();

  const targetDay = (januaryFirstDay + day) % 7;

  const daysToAddOrSubtract = targetDay - targetDate.getDay();

  targetDate.setDate(targetDate.getDate() + daysToAddOrSubtract);

  return targetDate;
}

const convertCamelCaseToSpace = (str: string): string => {
  const result = (str?.split(/(?=[A-Z])/) ?? [])
    .map((part) => part.toLowerCase())
    .join(" ");
  return result.replace(/\b\w/g, (char) => char.toUpperCase());
};

function replaceReportDateFilterCrossSvgWithReload() {
  let counter = 0;
  let intervalId = setInterval(() => {
    counter++;
    let clearButton = document.querySelector(
      ".react-datetimerange-picker__clear-button"
    );
    if (counter > 5) clearInterval(intervalId);
    if (!clearButton) return;
    clearButton.innerHTML = `<svg height="12px" width="12px"  viewBox="0 0 32 32">
    <path d="M27.802 5.197c-2.925-3.194-7.13-5.197-11.803-5.197-8.837 0-16 7.163-16 16h3c0-7.18 5.82-13 13-13 3.844 0 7.298 1.669 9.678 4.322l-4.678 4.678h11v-11l-4.198 4.197z"></path>
    <path d="M29 16c0 7.18-5.82 13-13 13-3.844 0-7.298-1.669-9.678-4.322l4.678-4.678h-11v11l4.197-4.197c2.925 3.194 7.13 5.197 11.803 5.197 8.837 0 16-7.163 16-16h-3z"></path>
    </svg>`;
    clearInterval(intervalId);
  });
}

const getLoadingPercentage = (length?: number, totalCount?: number) => {
  if (!totalCount) return 100;
  return Math.round(
    (Number(length ?? 0) / Number(totalCount)) * 100
  );
};

const daysObjects = [
  { name: "Monday", id: 0 },
  { name: "Tuesday", id: 1 },
  { name: "Wednesday", id: 2 },
  { name: "Thursday", id: 3 },
  { name: "Friday", id: 4 },
  { name: "Saturday", id: 5 },
  { name: "Sunday", id: 6 },
];

const transformFilterObjectToGqlVariables = (
  filterObjects: {},
  filterObjectMapper = {}
) => {
  let gqlVariablesObject = {};
  for (let filterKey in filterObjects) {
    let filterObject = filterObjects[filterKey];
    if (filterObjectMapper[filterKey]) {
      if (filterObject?.filterType == "text") {
        gqlVariablesObject[filterObjectMapper[filterKey]] =
          filterObject?.filter;
      } else if (filterObject?.filterType == "date") {
        gqlVariablesObject[filterObjectMapper[filterKey]] =
          filterObject?.dateFrom;
      }
    }
  }
  return gqlVariablesObject;
};

const enum LocalStorageKeys {
  ignoreGlobalAdmin = "ignoreGlobalAdmin",
  userADGroups = "userADGroups",
  currentUser = "currentUser",
}

const globalAdminGroup = "GRP Mizani YM GLB Users";

const formatDateTime = (
  dateString: string | undefined,
  time = false,
  twelveTimeZone = true
): string => {
  let date = "";
  if (!dateString) return date;
  if (time) {
    if (twelveTimeZone) {
      date = moment(dateString).format("DD/MM/YYYY hh:mm A");
    } else date = moment(dateString).format("DD/MM/YYYY HH:mm");
  } else {
    date = moment(dateString).format("DD/MM/YYYY");
  }

  return date;
};

enum OrderStatuses {
  Quotation = 1,
  OrderConfirmed,
  PaymentConfirmed,
  Dispatched,
  Delivered,
  Completed,
  Cancelled,
  PaymentSubmitted,
  SOGenerated,
  QuotationGenerated,
  QuotationConfirmed,
}

const getPaymentModeTranslation = (description: string) => {
  switch (description) {
    case "Manual":
      return "label.manual";
    case "Available Balance":
      return "label.available-balance";
    case "Bank Deposit - Cash":
      return "label.bank-deposit-cash";
    case "Bank Deposit - Cheque":
      return "label.bank-deposit-cheque";
    case "Bank Transfer":
      return "label.bank-transfer";
    case "Credit Order":
      return "label.credit-order";
    case "Hualage Claim":
      return "label.hualage-claim";
    case "DFS Credit Order":
      return "label.dfs";
    case "E-Payment":
      return "label.e-payment";
    case "Bank Deposit":
      return "label.bank-deposit";
    case "Endorsed Draft":
      return "label.endorsed-draft";
    case "":
      return "N/A";
    case null:
      return "N/A";
    default:
      return description;
  }
};

const OrderStatusTranslation = (status: string) => {
  switch (status) {
    case "Quotation":
      return "salesvoucherstatusenum.quotation";
    case "Quotation Created":
      return "salesvoucherstatusenum.quotation";
    case "Waiting for Quotation":
      return "modules.content.orders.waiting-for-quotation";
    case "Dispatched":
      return "salesvoucherstatusenum.dispatched";
    case "Order Confirmed":
      return "salesvoucherstatusenum.orderconfirmed";
    case "Payment Confirmed":
      return "salesvoucherstatusenum.paymentconfirmed";
    case "Cancelled":
      return "salesvoucherstatusenum.cancelled";
    case "Delivered":
      return "salesvoucherstatusenum.delivered";
    case "Completed":
      return "salesvoucherstatusenum.completed";
    case "Pending":
      return "salesvoucherstatusenum.pending";
    case "Payment Submitted":
      return "salesvoucherstatusenum.paymentsubmitted";
    case "SOGenerated":
      return "salesvoucherstatusenum.sogenerated";
    default:
      return status;
  }
};

const summaryTitleTranslation = (title: string) => {
  switch (title) {
    case "Approved Payment":
      return "labels.approved-payment";
    case "Pending Payment":
      return "labels.pending-payment";
    case "Approved Orders":
      return "labels.approved-orders";
    case "Quotations":
      return "labels.quotations";
    case " Approved Payment":
      return "labels.approved-payment";
    case " Pending Payment":
      return "labels.pending-payment";
    case " Approved Orders":
      return "labels.approved-orders";
    case " Quotations":
      return "labels.quotations";
    default:
      return title;
  }
};

const getOrderStatusTextColor = (orderStatus: string) => {
  const statusKey = orderStatus?.replace(/\s+/g, "")?.replace(/Created$/, "");

  switch (statusKey) {
    case "OrderConfirmed":
    case "SOGenerated":
      return "#A5AF37";
    case "Completed":
      return "#0BA047";
    case "Delivered":
      return "#00DA09";
    case "PaymentConfirmed":
      return "#015FBF";
    case "Cancelled":
      return "#DA0901";
    case "Dispatched":
      return "#D49901";
    case "Quotation":
    case "WaitingforQuotation":
      return "#FF8A1E";
    case "voided":
      return "#FF1E22";
    default:
      return "#667085";
  }
};

const getSalesVoucherStatusTextColor = (salesVoucherStatus: number) => {
  const statusColorMap = {
    [SalesVoucherStatusEnum.Quotation]: {
      backgroundColor: "#FEF9F2",
      textColor: "#EE8B00",
      borderColor: "#FCEED9",
    },
    [SalesVoucherStatusEnum.SalesOrderConfirmed]: {
      backgroundColor: "#FEF2FF",
      textColor: "#A620AF",
      borderColor: "#FBCFFF",
    },
    [SalesVoucherStatusEnum.PaymentConfirmed]: {
      backgroundColor: "#F2F8FD",
      textColor: "#016AD4",
      borderColor: "#E2EFFA",
    },
    [SalesVoucherStatusEnum.PaymentSubmitted]: {
      backgroundColor: "#FEFFED",
      textColor: "#A5AF37",
      borderColor: "#F4F7C4",
    },
    [SalesVoucherStatusEnum.SalesOrderDispatched]: {
      backgroundColor: "#ECFEFF",
      textColor: "#09A7AF",
      borderColor: "#C6FCFF",
    },
    [SalesVoucherStatusEnum.Delivered]: {
      backgroundColor: "#F2FFFB",
      textColor: "#026D51",
      borderColor: "#CFE3DE",
    },
    [SalesVoucherStatusEnum.Completed]: {
      backgroundColor: "#F2FBF6",
      textColor: "#00AB50",
      borderColor: "#D9F2E5",
    },
    [SalesVoucherStatusEnum.SOGenerated]: {
      backgroundColor: "#F0E9FF",
      textColor: "#7239EA",
      borderColor: "#D9C7FF",
    },
    [SalesVoucherStatusEnum.Voided]: {
      backgroundColor: "#FDF3F2",
      textColor: "#DA0901",
      borderColor: "#F9DAD9",
    },
    [SalesVoucherStatusEnum.CancellationRequested]: {
      backgroundColor: "#FFFAEB",
      textColor: "#B54708",
      borderColor: "#FEDF89",
    },
    [SalesVoucherStatusEnum.ProcessingCancellationRequest]: {
      backgroundColor: "#EFF8FF",
      textColor: "#175CD3",
      borderColor: "#B2DDFF",
    },
  };

  return (
    statusColorMap[salesVoucherStatus] || {
      backgroundColor: "",
      textColor: "",
      borderColor: "",
    }
  );
};

const getPaymentStatusTextColor = (paymentStatus: number) => {
  let colorScheme = {
    textColor: "",
    backgroundColor: "",
    borderColor: "",
  };
  if (
    paymentStatus === PaymentStatusEnum.Approved ||
    paymentStatus === PaymentStatusEnum.Closed
  ) {
    colorScheme.backgroundColor = "#F2FFFB";
    colorScheme.textColor = "#00843D";
    colorScheme.borderColor = "#CFE3DE";
  } else if (paymentStatus === PaymentStatusEnum.FinalApprovalRequired) {
    colorScheme.backgroundColor = "#FEF8F1";
    colorScheme.textColor = "#D67900";
    colorScheme.borderColor = "#F8C481";
  } else if (paymentStatus === PaymentStatusEnum.Pending) {
    colorScheme.backgroundColor = "#FEF9F2";
    colorScheme.textColor = "#EE8B00";
    colorScheme.borderColor = "#FCEED9";
  } else if (
    paymentStatus === PaymentStatusEnum.Rejected ||
    paymentStatus === PaymentStatusEnum.Cancelled
  ) {
    colorScheme.backgroundColor = "#FDF3F2";
    colorScheme.textColor = "#DA0901";
    colorScheme.borderColor = "#F9DAD9";
  } else if (paymentStatus === PaymentStatusEnum.TranscriptionRequired) {
    colorScheme.backgroundColor = "#FBFAE5";
    colorScheme.textColor = "#A9A106";
    colorScheme.borderColor = "#CAC89C";
  } else if (paymentStatus === PaymentStatusEnum.TreasurerApprovalRequired) {
    colorScheme.backgroundColor = "#FCF7FF";
    colorScheme.textColor = "#8F26E0";
    colorScheme.borderColor = "#CF9AF8";
  }
  else if (paymentStatus === PaymentStatusEnum.SalesOfficerApprovalRequired) {
    colorScheme.backgroundColor = "#e7e7cfff";
    colorScheme.textColor = "#686300ff";
    colorScheme.borderColor = "#878568ff";
  }
  return colorScheme;
};

const predefinedDateRanges = (t: (key: string) => string) => [
  {
    label: t("labels.today"),
    value: [
      new Date(new Date().setHours(0, 0, 0, 0)),
      new Date(new Date().setHours(23, 59, 59, 999)),
    ] as DateRange,
  },

  {
    label: t("labels.yesterday"),
    value: [
      new Date(
        new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
          0,
          0,
          0,
          0
        )
      ),
      new Date(
        new Date(new Date().setDate(new Date().getDate() - 1)).setHours(
          23,
          59,
          59,
          999
        )
      ),
    ] as DateRange,
  },

  {
    label: t("labels.last-7-days"),
    value: [
      new Date(new Date().setDate(new Date().getDate() - 6)),
      new Date(),
    ] as DateRange,
  },
  {
    label: t("labels.last-30-days"),
    value: [
      new Date(new Date().setDate(new Date().getDate() - 29)),
      new Date(),
    ] as DateRange,
  },
  {
    label: t("labels.this-quarter"),
    value: [
      new Date(
        new Date().getFullYear(),
        Math.floor(new Date().getMonth() / 3) * 3,
        1
      ),
      new Date(),
    ] as DateRange,
  },
  {
    label: t("labels.year-to-date"),
    value: [new Date(new Date().getFullYear(), 0, 1), new Date()] as DateRange,
  },
];

function strToArrayBuffer(str) {
  return new TextEncoder().encode(str);
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const uint8Array = new Uint8Array(buffer);
  let binary = "";
  const len = uint8Array.length;
  for (let i = 0; i < len; i++) {
    binary += String.fromCodePoint(uint8Array[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.codePointAt(i) ?? 0;
  }
  return bytes.buffer;
}

async function generateKey() {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, [
    "encrypt",
    "decrypt",
  ]);
}

async function encryptString(key, data) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = strToArrayBuffer(data);
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv },
    key,
    encoded
  );
  return arrayBufferToBase64(iv.buffer) + ":" + arrayBufferToBase64(ciphertext);
}

async function decryptString(key, data) {
  if (!data?.includes(":")) {
    return null;
  }
  const [ivB64, cipherB64] = data.split(":");
  const iv = base64ToArrayBuffer(ivB64);
  const ciphertext = base64ToArrayBuffer(cipherB64);
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: new Uint8Array(iv) },
    key,
    ciphertext
  );
  return new TextDecoder().decode(decrypted);
}

export {
  generateKey,
  encryptString,
  decryptString,
  getPaymentModeTranslation,
  OrderStatusTranslation,
  summaryTitleTranslation,
  LocalStorageKeys,
  globalAdminGroup,
  startOfDay,
  endOfDay,
  persistLastPOLocation,
  getLastPOLocation,
  flattenObj,
  dateComparator,
  objectDateComparator,
  dateFormatter,
  arraysContainSameStrings,
  timeFilterComparator,
  _monthToNum,
  toTitleCase,
  paddObject,
  wait,
  attachGridScrollEvent,
  filterComparator,
  formattedPTTime,
  formatNumber,
  compareObjects,
  convertToISO8601,
  convertfromISO8601,
  ellipsiceText,
  calculateTimeDifference,
  getWeeksOfYear,
  getWeekNumber,
  delayedReset,
  getDay,
  getDateFromWeek,
  replaceReportDateFilterCrossSvgWithReload,
  convertCamelCaseToSpace,
  getLoadingPercentage,
  calculateTimeDifferenceFormatted,
  calculateTimeDifferenceInMinutes,
  daysObjects,
  convertFromPascal,
  transformFilterObjectToGqlVariables,
  exportExcel,
  getPdfPrintParams,
  formatDateTime,
  getOrderStatusTextColor,
  getSalesVoucherStatusTextColor,
  getPaymentStatusTextColor,
  predefinedDateRanges,
  strToArrayBuffer,
  arrayBufferToBase64,
  base64ToArrayBuffer,
  getDateFromWeekxx,
  OrderStatuses,
  checkIsDeliveryInclusive,
};
